import styled from '@emotion/styled';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import HighlightedText from '~/common/components/HighlightedText';
import StatusIcon from '~/common/components/Icons/StatusIcon';
import useLeftDrawerContext from '~/common/components/Nav/LeftDrawer/context/useLeftDrawerContext';
import Text from '~/common/components/Texts/Text';
import { ALERT_TYPES, RULE_RESULT_STATUS_ID } from '~/common/constants';
import { useUser } from '~/common/hooks/useAuthentication';
import { useNavSearchStore } from '~/stores/useNavSearchStore';

import { alertStateAttributes } from './alertStateAttributes';
import { HomeActions } from './redux';

const StyledChildAlert = styled<any>('button')`
  cursor: pointer;
  display: flex;
  transition: all 0.5s ease-in-out;
  align-items: center;
  padding-left: 1.75rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-right: 0.25rem;
  width: 100%;
  min-height: 48px;
  background-color: ${(props) =>
    props.selected ? props.theme.colors?.blue[100] : props.theme.colors?.white};
  border-bottom: 1px solid ${(props) => props.theme.colors?.gray[200]};
  border-right: ${(props) =>
    props.selected
      ? `4px solid ${props.theme.colors?.blue[600]}`
      : props.theme.colors?.white};

  &:hover {
    background-color: ${(props) => props.theme.colors?.blue[50]};
  }

  &:focus {
    box-shadow: none;
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${(props) => props.theme.colors?.gray[100]};
    outline: 0;
  }
`;

type ChildAlertProps = {
  alert?: any;
  selectedAlert?: any;
  parentAlert?: any;
  circleIcon?: boolean;
  status?: (typeof RULE_RESULT_STATUS_ID)[keyof typeof RULE_RESULT_STATUS_ID];
};

const ChildAlert = ({
  parentAlert,
  alert,
  status,
  circleIcon,
}: Readonly<ChildAlertProps>) => {
  const dispatch = useDispatch();
  const searchQuery = useNavSearchStore((s) => s.searchQuery);
  const { handleLeftDrawerCollapseForChildAlert } = useLeftDrawerContext();
  const { selectedAlert, selectedAlertParent } = useSelector(
    ({ home }) => home,
  );
  const { isUnderwriter, hasAccessToUnderwriterView } = useUser();
  const isSelected = alert?.gutterItemId === selectedAlert?.gutterItemId;
  const alertStatus = status ?? alert?.ruleResultStatusId;

  useEffect(() => {
    if (
      alert?.gutterItemId === selectedAlert?.gutterItemId &&
      selectedAlertParent?.gutterItemId !== parentAlert?.gutterItemId
    ) {
      dispatch(HomeActions.setSelectedAlertParent(parentAlert));
    }
  }, [alert, dispatch, parentAlert, selectedAlert, selectedAlertParent]);

  const textStyles = {
    ml: 2,
    overflow: 'hidden',
    letterSpacing: '0px',
    width: '125px',
    fontSize: '12px',
    textAlign: 'left',
    fontWeight: isSelected ? '500' : '400',
    lineHeight: '1rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    opacity: 1,
    transform: 'translateX(0)',
    transition: 'all 0.5s ease-in-out',
  };

  return (
    <StyledChildAlert
      title={alert?.displayTitle}
      role="tab"
      selected={isSelected}
      onClick={() =>
        handleLeftDrawerCollapseForChildAlert(
          alert,
          selectedAlert as any,
          dispatch as any,
        )
      }
      data-testid="child-alert"
      aria-selected={isSelected}
      {...alertStateAttributes(alert, selectedAlert, alertStatus)}
      data-tracking={`Child-${alert?.displayTitle}`}
    >
      <StatusIcon
        status={alertStatus}
        canSeeVerification={isUnderwriter || hasAccessToUnderwriterView}
        type={circleIcon ? ALERT_TYPES.REVIEW : ALERT_TYPES.CHILD}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Text as="h4" marginLeft="11px" sx={textStyles}>
          <HighlightedText
            text={alert?.displayTitle ?? ''}
            query={searchQuery}
          />
        </Text>
        {alert?.displaySubTitle && (
          <Text
            as="h5"
            marginLeft="11px"
            sx={{
              ...textStyles,
              fontWeight: '400',
              fontSize: '10px',
            }}
          >
            {alert?.displaySubTitle}
          </Text>
        )}
      </div>
    </StyledChildAlert>
  );
};

export default ChildAlert;
